"use client";

import Link from "next/link";
import { useMemo, useState } from "react";

type ResultType = {
  name: string;
  label: string;
  description: string;
  nextStep: string;
};

type Answers = Record<string, number>;

const quizQuestions = [
  {
    text: "I gain weight easily even when I feel like I’m not eating that much",
    category: "storer",
    weight: 3,
  },
  {
    text: "I get strong cravings for sugar, bread, or snacks later in the day",
    category: "crash_crave",
    weight: 3,
  },
  {
    text: "I can be ‘good’ all week and still not see the scale move",
    category: "stubborn",
    weight: 3,
  },
  {
    text: "I feel hungry again not long after eating",
    category: "crash_crave",
    weight: 2,
  },
  {
    text: "I store most of my weight around my middle",
    category: "storer",
    weight: 3,
  },
  {
    text: "When I try to diet harder, I end up overeating later",
    category: "crash_crave",
    weight: 3,
  },
  {
    text: "My energy feels unstable through the day",
    category: "crash_crave",
    weight: 2,
  },
  {
    text: "I feel like my body is resisting weight loss",
    category: "stubborn",
    weight: 3,
  },
  {
    text: "Healthy foods like smoothies, granola, or fruit-heavy breakfasts can still leave me hungry",
    category: "storer",
    weight: 2,
  },
  {
    text: "I’ve lost weight before, but it always comes back",
    category: "stubborn",
    weight: 3,
  },
  {
    text: "I need snacks, caffeine, or something sweet to keep going",
    category: "crash_crave",
    weight: 2,
  },
  {
    text: "I feel frustrated because I’m putting in effort without the result matching it",
    category: "stubborn",
    weight: 2,
  },
] as const;

const quizResults: ResultType[] = [
  {
    name: "storer",
    label: "Burner vs Storer",
    description:
      "Your body may be prioritising storage over burn mode. That usually means your hunger, insulin response, and meal structure are working against you — not your motivation.",
    nextStep:
      "Build meals around protein, fibre, and steadier blood sugar signals so your body stops treating fat loss like a threat.",
  },
  {
    name: "crash_crave",
    label: "Crash & Crave Type",
    description:
      "Your biology may be pushing you through a cycle of spikes, dips, hunger, and rebound eating. The problem is not weakness. The system is creating the craving.",
    nextStep:
      "Reduce the blood sugar swings first. Once the crashes calm down, hunger and food noise usually drop with them.",
  },
  {
    name: "stubborn",
    label: "Stubborn Weight Type",
    description:
      "Your body may have adapted to repeated restriction, stress, and mixed metabolic signals. That can make fat loss feel unusually hard, even when you’re trying.",
    nextStep:
      "Stop forcing harder restriction. Restore clearer signals around blood sugar, gut health, and appetite regulation so your metabolism becomes responsive again.",
  },
];

const proofCards = [
  {
    title: "It’s not a willpower problem",
    body: "If your body is driving hunger, storing energy aggressively, and triggering cravings, discipline alone will always feel unreliable.",
  },
  {
    title: "Dieting can make it worse",
    body: "Eating less often looks logical, but for many people it amplifies cravings, energy crashes, rebound eating, and weight regain.",
  },
  {
    title: "Your signals matter more than calories alone",
    body: "Blood sugar, gut feedback, and brain-based appetite signals help determine whether your body feels safe to burn fat or compelled to hold onto it.",
  },
];

const insulinFoods = [
  "Granola and ‘healthy’ cereals",
  "Fruit-heavy breakfasts and smoothies",
  "Low-fat yoghurts with hidden sugar",
  "Rice cakes, oat bars, and grazing snacks",
  "Salads that are too low in protein",
  "Juices and ‘natural’ energy drinks",
];

const faqs = [
  {
    q: "Is this about cutting calories harder?",
    a: "No. This page is about why harder restriction often backfires, and why fixing blood sugar, gut, and appetite signals creates a much more sustainable route to fat loss.",
  },
  {
    q: "What happens after the quiz?",
    a: "You’ll see which pattern fits you most closely and get a practical next step, plus an invitation to the Metabolic Reset Program if it feels like the right fit.",
  },
  {
    q: "Who is this for?",
    a: "It’s designed for people who feel like they’re doing ‘all the right things’ but still struggle with cravings, energy dips, or stubborn fat loss.",
  },
] as const;

export default function FunnelTwoLandingPage() {
  const [answers, setAnswers] = useState<Answers>({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<ResultType | null>(null);
  const [error, setError] = useState("");

  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);

  const setAnswer = (key: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const calculateResult = (): ResultType => {
    const scores = {
      storer: 0,
      crash_crave: 0,
      stubborn: 0,
    };

    quizQuestions.forEach((question) => {
      const key = slugify(question.text);
      const answer = answers[key] ?? 0;
      scores[question.category] += answer * question.weight;
    });

    const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const [topCategory] = ranked[0];

    return quizResults.find((item) => item.name === topCategory) ?? quizResults[0];
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!name.trim() || !email.trim()) {
      setError("Please enter your name and email.");
      return;
    }

    if (answeredCount === 0) {
      setError("Please answer at least one question.");
      return;
    }

    setError("");
    setResult(calculateResult());
  };

  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)] overflow-x-hidden">
      <section
        className="relative isolate border-b border-black/10 text-white"
        style={{ background: "radial-gradient(circle at top, color-mix(in srgb, var(--coral-dk) 18%, transparent), transparent 38%), linear-gradient(180deg, var(--ink) 0%, var(--ink-80) 55%, var(--ink) 100%)" }}
      >
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-6 sm:px-8 lg:px-10">
          <div className="mb-14 flex items-center justify-between gap-4">
            <div className="text-sm font-semibold uppercase tracking-[0.24em] text-white/78">
              Metabolic Reset
            </div>
            <Link
              href="/"
              className="rounded-full border border-white/15 px-5 py-2 text-sm text-white/82 transition hover:border-white/30 hover:bg-white/5"
            >
              Main site
            </Link>
          </div>

          <div className="grid items-start gap-10 lg:grid-cols-[1.08fr_0.92fr]">
            <div className="max-w-3xl">
              <div className="mb-6 inline-flex items-center rounded-full border border-white/12 bg-white/6 px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-[var(--apricot-lt)]">
                Fat loss without restriction
              </div>

              <h1 className="max-w-4xl text-[clamp(2.7rem,6vw,5.8rem)] font-semibold leading-[0.96] tracking-[-0.04em] text-white">
                If you can’t lose weight,
                <span className="block text-[var(--apricot-lt)]">it’s not calories.</span>
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-8 text-white/76 sm:text-xl">
                Eating less is often making it worse. If the weight keeps coming back, the issue is usually not willpower — it’s the metabolic signals telling your body to hold onto fat.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {[
                  "Why dieting backfires and rebounds harder",
                  "Why cravings are biology, not weakness",
                  "How ‘healthy’ foods can still spike insulin",
                  "Why fat loss gets easier when signals stabilise",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-white/12 bg-white/6 px-4 py-4 text-sm leading-6 text-white/82 backdrop-blur"
                  >
                    {item}
                  </div>
                ))}
              </div>

              <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <a
                  href="#quiz"
                  className="inline-flex rounded-full bg-[var(--apricot-lt)] px-8 py-4 text-sm font-semibold uppercase tracking-[0.08em] text-[var(--ink)] transition hover:-translate-y-0.5"
                >
                  Take the metabolism type quiz
                </a>
                <p className="text-sm italic text-white/58">
                  2 minutes · instant result · no calorie counting lecture
                </p>
              </div>
            </div>

            <aside className="rounded-[28px] border border-white/12 bg-[var(--paper)] p-6 text-[var(--ink)] shadow-2xl shadow-black/20 sm:p-7 lg:mt-5">
              <h2 className="mt-3 text-3xl font-semibold leading-tight tracking-[-0.03em]">
                Your body is not broken.
                <span className="block text-[var(--coral-text)]">It’s getting the wrong signals.</span>
              </h2>
              <div className="mt-6 space-y-3 text-[0.98rem] leading-7 text-[var(--ink-80)]">
                <p>
                  When blood sugar is unstable, your gut is inflamed, and your brain keeps receiving hunger signals, fat loss feels like a fight.
                </p>
                <p>
                  Fix those signals, and your body stops clinging to fat quite so aggressively.
                </p>
              </div>
              <div className="mt-7 rounded-2xl bg-[var(--ink)] p-5 text-[var(--text-on-dark)]">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--apricot-lt)]">
                  Mechanism
                </p>
                <p className="mt-2 text-sm leading-6 text-[var(--text-on-dark-muted)]">
                  Blood sugar + gut + brain signals
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="border-b border-black/8 bg-[var(--cream-dk)] px-6 py-4 sm:px-8 lg:px-10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[0.76rem] font-semibold uppercase tracking-[0.14em] text-[var(--ink-80)]">
          {[
            "No willpower shaming",
            "No restriction-first advice",
            "No generic calorie talk",
            "No more guessing",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </section>

      <section className="bg-[var(--cream)] px-6 py-20 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <h2 className="mt-3 text-[clamp(2rem,4vw,3.5rem)] font-semibold leading-[1.02] tracking-[-0.04em] text-[var(--ink)]">
              You do not have a willpower problem.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-[var(--ink-80)]">
              Most people are trying to solve a hormone-and-signal problem with more discipline. That is why the effort feels high, the cravings stay loud, and the weight comes back.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {proofCards.map((card) => (
              <div key={card.title} className="rounded-[28px] border border-black/8 bg-white px-6 py-7 shadow-[0_12px_50px_rgba(36,24,18,0.05)]">
                <h3 className="text-xl font-semibold tracking-[-0.02em] text-[var(--ink)]">
                  {card.title}
                </h3>
                <p className="mt-3 text-[0.98rem] leading-7 text-[var(--ink-80)]">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--ink)] px-6 py-20 text-white sm:px-8 lg:px-10">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <div>
            <p className="text-[0.75rem] font-semibold uppercase tracking-[0.18em] text-[var(--apricot-lt)]">
              Insulin resistance, explained simply
            </p>
            <h2 className="mt-3 text-[clamp(2rem,4vw,3.2rem)] font-semibold leading-[1.04] tracking-[-0.04em] text-white">
              Your body starts storing when it stops trusting the signal.
            </h2>
            <p className="mt-6 text-[1.02rem] leading-8 text-white/72">
              If insulin is elevated too often, your body gets less efficient at using incoming energy. That can leave you more hungry, more crash-prone, and more likely to store fat even while trying to eat “well.”
            </p>
            <p className="mt-5 text-[1.02rem] leading-8 text-white/72">
              So the goal is not to punish your body harder. The goal is to create steadier input so the system calms down and fat burning becomes easier again.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--apricot-lt)]">
                Why dieting backfires
              </p>
              <ul className="mt-4 space-y-3 text-sm leading-7 text-white/78">
                <li>• Hunger goes up</li>
                <li>• Cravings get louder</li>
                <li>• Energy becomes less stable</li>
                <li>• Rebound eating becomes more likely</li>
                <li>• Weight regain feels inevitable</li>
              </ul>
            </div>
            <div className="rounded-[28px] border border-white/10 bg-[var(--apricot-lt)] p-6 text-[var(--ink)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--coral-text)]">
                Cravings are signals
              </p>
              <p className="mt-4 text-sm leading-7 text-[var(--ink-80)]">
                Cravings are often a downstream effect of unstable blood sugar, stress signalling, poor satiety, and gut-driven appetite disruption.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--paper)] px-6 py-20 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <h2 className="mt-3 text-[clamp(2rem,4vw,3.1rem)] font-semibold leading-[1.05] tracking-[-0.04em] text-[var(--ink)]">
              “Healthy” foods can still keep you stuck.
            </h2>
            <p className="mt-5 text-[1.02rem] leading-8 text-[var(--ink-80)]">
              Plenty of foods marketed as clean or healthy still create the exact spike-crash-hunger loop that makes fat loss harder.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {insulinFoods.map((item) => (
              <div key={item} className="rounded-2xl border border-black/8 bg-white px-5 py-5 text-[0.98rem] font-medium leading-7 text-[var(--ink)] shadow-[0_8px_28px_rgba(36,24,18,0.04)]">
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="quiz" className="bg-[var(--cream-dk)] px-6 py-20 sm:px-8 lg:px-10">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div className="lg:sticky lg:top-6 lg:self-start">
            <h2 className="mt-3 text-[clamp(2rem,4vw,3.1rem)] font-semibold leading-[1.04] tracking-[-0.04em] text-[var(--ink)]">
              Metabolism Type Quiz
            </h2>
            <p className="mt-5 text-[1rem] leading-8 text-[var(--ink-80)]">
              Answer a few quick questions and see which pattern fits you best.
            </p>

            <div className="mt-7 space-y-3">
              {quizResults.map((item) => (
                <div key={item.label} className="rounded-2xl border border-black/8 bg-white/70 px-5 py-4 text-sm leading-6 text-[var(--ink-80)]">
                  <span className="font-semibold text-[var(--ink)]">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[32px] border border-black/8 bg-white p-6 shadow-[0_20px_70px_rgba(36,24,18,0.08)] sm:p-8">
            <h3 className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-[var(--ink)]">
              Why is your body holding onto fat?
            </h3>
            <p className="mt-3 text-[0.98rem] leading-7 text-[var(--ink-80)]">
              Score each statement from 0 to 3.
              <br />0 = never · 1 = sometimes · 2 = often · 3 = almost always
            </p>

            <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
              {quizQuestions.map((question) => {
                const key = slugify(question.text);
                const currentValue = answers[key];

                return (
                  <div key={question.text} className="rounded-2xl border border-[var(--border-on-light)] bg-[var(--paper)] p-4 sm:p-5">
                    <p className="text-[0.98rem] leading-7 text-[var(--ink)]">{question.text}</p>
                    <div className="mt-4 grid grid-cols-4 gap-2">
                      {[0, 1, 2, 3].map((value) => {
                        const active = currentValue === value;

                        return (
                          <label
                            key={value}
                            htmlFor={`${key}-${value}`}
                            className={`cursor-pointer rounded-xl border px-3 py-3 text-center text-sm font-medium transition ${
                              active
                                ? "border-[var(--coral-dk)] bg-[var(--coral-dk)] text-[var(--on-lead)] shadow-lg"
                                : "border-[var(--border-on-light)] bg-white text-[var(--ink-80)] hover:bg-[var(--cream)]"
                            }`}
                          >
                            <input
                              id={`${key}-${value}`}
                              type="radio"
                              name={key}
                              value={value}
                              checked={currentValue === value}
                              onChange={() => setAnswer(key, value)}
                              className="sr-only"
                            />
                            {value}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="rounded-[28px] bg-[var(--ink)] p-5 sm:p-6">
                <h4 className="text-2xl font-semibold tracking-[-0.03em] text-white">See your result</h4>
                <p className="mt-3 text-sm leading-7 text-white/72">
                  Enter your details to reveal your metabolism type and the first thing to fix.
                </p>

                <div className="mt-5 grid gap-3">
                  <input
                    type="text"
                    placeholder="First name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="rounded-xl border border-white/10 bg-white px-4 py-3 text-[var(--ink)] outline-none placeholder:text-[var(--ink-80)]"
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded-xl border border-white/10 bg-white px-4 py-3 text-[var(--ink)] outline-none placeholder:text-[var(--ink-80)]"
                  />
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-full bg-[var(--apricot-lt)] px-6 py-4 text-sm font-semibold uppercase tracking-[0.08em] text-[var(--ink)] transition hover:-translate-y-0.5"
                  >
                    Show my metabolism type
                  </button>
                </div>

                {error ? <p className="mt-3 text-sm text-[var(--accent-light)]">{error}</p> : null}

                <p className="mt-3 text-xs italic text-white/48">
                  By signing up, you’ll also receive follow-up guidance on stabilising hunger, energy, and fat loss.
                </p>
              </div>

              {result ? (
                <div className="rounded-[28px] border border-[var(--border-on-light)] bg-[var(--paper)] p-6">
                  <p className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-[var(--coral-text)]">
                    Your result
                  </p>
                  <h4 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-[var(--ink)]">
                    {result.label}
                  </h4>
                  <p className="mt-4 text-[0.98rem] leading-7 text-[var(--ink-80)]">{result.description}</p>

                  <div className="mt-5 rounded-2xl bg-[var(--cream-dk)] p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--coral-text)]">
                      Start here first
                    </p>
                    <p className="mt-2 text-sm leading-7 text-[var(--ink-80)]">{result.nextStep}</p>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-4">
                    <Link
                      href="/workwithme/discoverycall"
                      className="inline-flex items-center justify-center rounded-full bg-[var(--coral-dk)] px-6 py-4 text-sm font-semibold uppercase tracking-[0.08em] text-[var(--on-lead)] transition hover:-translate-y-0.5"
                    >
                      Book a free discovery call
                    </Link>
                  </div>
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </section>

      <section className="bg-[var(--cream)] px-6 py-20 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <h2 className="mt-3 text-[clamp(2rem,4vw,3.3rem)] font-semibold leading-[1.04] tracking-[-0.04em] text-[var(--ink)]">
              Fix this, and fat loss becomes much more automatic.
            </h2>
            <p className="mt-5 text-[1rem] leading-8 text-[var(--ink-80)]">
              When your body gets better signals, it stops fighting you at every step. Hunger comes down. Energy steadies. The all-day food noise quiets. That is the point where fat loss stops feeling like a constant battle.
            </p>
          </div>

          <div className="mt-10 rounded-[32px] border border-black/8 bg-[var(--ink)] p-8 text-white shadow-[0_24px_90px_rgba(36,24,18,0.16)] sm:p-10">
            <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr] lg:items-center">
              <div>
                <h3 className="mt-3 text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.04] tracking-[-0.04em]">
                  Metabolic Reset Program
                </h3>
                <p className="mt-5 max-w-2xl text-[1rem] leading-8 text-white/72">
                  A structured process to help you lose fat without restriction, regulate hunger, stabilise energy, and finally address the real mechanisms behind stubborn weight.
                </p>
              </div>

              <div className="rounded-[28px] bg-white/6 p-6 backdrop-blur">
                <ul className="space-y-3 text-sm leading-7 text-white/80">
                  <li>• Fat loss without living in restriction</li>
                  <li>• Better hunger regulation and fewer cravings</li>
                  <li>• Stable energy instead of spikes and crashes</li>
                  <li>• A clearer framework for blood sugar, gut, and appetite repair</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                href="/workwithme/discoverycall"
                className="inline-flex rounded-full border border-white/14 px-8 py-4 text-sm font-semibold uppercase tracking-[0.08em] text-white transition hover:-translate-y-0.5 hover:bg-white/5"
              >
                Speak to me first
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--cream)] px-6 py-20 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <h2 className="mt-3 text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.05] tracking-[-0.04em] text-[var(--ink)]">
              Questions before you start
            </h2>
          </div>

          <div className="mt-10 space-y-4">
            {faqs.map((faq) => (
              <details key={faq.q} className="rounded-2xl border border-black/8 bg-white px-6 py-5">
                <summary className="cursor-pointer list-none text-lg font-semibold tracking-[-0.02em] text-[var(--ink)]">
                  {faq.q}
                </summary>
                <p className="mt-3 text-[0.98rem] leading-7 text-[var(--ink-80)]">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--ink)] px-6 py-24 text-white sm:px-8 lg:px-10">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mt-4 text-[clamp(2.2rem,5vw,4.2rem)] font-semibold leading-[0.98] tracking-[-0.05em] text-white">
            When your metabolism works,
            <span className="block text-[var(--apricot-lt)]">fat loss stops being a fight.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-[1rem] leading-8 text-white/72">
            You do not need more restriction. You need the right signals, the right mechanism, and the right structure.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#quiz"
              className="inline-flex rounded-full bg-[var(--apricot-lt)] px-8 py-4 text-sm font-semibold uppercase tracking-[0.08em] text-[var(--ink)] transition hover:-translate-y-0.5"
            >
              Take the quiz now
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

function slugify(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}
